import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';

import InputAdornment from '@material-ui/core/InputAdornment';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container/Container';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import compose from 'helpers/compose';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import FileUpload from 'components/FormFields/FileUpload';
import Background from 'components/Layout/Background';

import { Currency } from 'enums';
import { Method } from 'stores/apiStore';
import { RootStore } from 'stores/rootStore';

interface Props {
  importMap: {
    [key: string]: {
      heading: string;
      endpoint: string | ((type: string, currency: Currency, hedgingEur: string, hedgingUsd: string) => string);
      entityText: string;
      successText?: string;
      typeText?: string;
      typeValues?: { value: string; label: string }[];
      currencyText?: string;
      currencyValues?: { value: Currency; label: Currency | string }[];
      hedgingText?: string;
    };
  };
}

interface Stores {
  rootStore: RootStore;
}

const Import: React.FC<Props & Stores> = ({ rootStore, importMap }) => {
  const { t } = useTranslation();
  const [openedDialog, setDialogOpen] = useState<string | null>(null);
  const actualEntity = importMap[openedDialog as keyof typeof importMap];
  const { apiStore } = rootStore.stores;
  const {
    stores: { errorStore },
  } = rootStore;
  const [uploadFile, setUploadFile] = useState<null | File>(null);
  const [successDialog, setSuccessDialog] = useState(false);
  const [successDialogText, setSuccessDialogText] = useState('');
  const [types, setTypes] = useState<{ [key: string]: string }>({});
  const [currency, setCurrency] = useState<{ [key: string]: Currency }>({});
  const [hedgingEur, setHedgingEur] = useState([100, 100, 100, 100, 100, 100, 100, 100, 100, 100]);
  const [hedgingUsd, setHedgingUsd] = useState([100, 100, 100, 100, 100, 100, 100, 100, 100, 100]);

  const onImportChange = (e: any, key: string) => {
    setUploadFile(e.target.files[0]);
    setDialogOpen(key);
  };

  const dialogConfirmed = async (key: string | null) => {
    setDialogOpen(null);
    const { endpoint } = actualEntity;

    const formData = new FormData();
    formData.append('filename', uploadFile!);
    const ep =
      key && types[key] && typeof endpoint !== 'string'
        ? endpoint(
            types[key],
            currency[key],
            hedgingEur.map((h) => h / 100).join(','),
            hedgingUsd.map((h) => h / 100).join(','),
          )
        : (endpoint as string);

    const response = await apiStore.handleRequest<File>(ep, Method.POST, formData, {
      method: Method.POST,
      body: formData,
      headers: {
        Authorization: `Bearer ${apiStore.authToken}`,
        // No Content-Type: https://stackoverflow.com/a/49510941
      },
    });
    if (response?.errors) {
      response.errors.forEach((error) => errorStore.addGlobalError(error.message || error.key));
    } else {
      setSuccessDialogText(actualEntity.successText || t('admin_uploads_dataSaved'));
      setSuccessDialog(true);
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={!!openedDialog}
        close={() => {
          setDialogOpen(null);
          setUploadFile(null);
        }}
        confirm={() => dialogConfirmed(openedDialog)}
        title="admin_uploads_confirmHeading"
      >
        {t('admin_uploads_confirmBody')
          .replace('{file}', (uploadFile && uploadFile.name) || '')
          .replace('{entity}', (actualEntity && actualEntity.entityText) || '')}
      </ConfirmDialog>

      <ConfirmDialog noHeader isOpen={successDialog} confirm={() => setSuccessDialog(false)}>
        {successDialogText}
      </ConfirmDialog>

      <Container maxWidth="md">
        <Background inside>
          <Grid container>
            {Object.entries(importMap).map(([key, value]) => (
              <React.Fragment key={key}>
                <Grid item xs={2}>
                  <Typography variant="h3">{value.heading}</Typography>
                </Grid>
                <Grid container item xs={6}>
                  <Grid item xs={5}>
                    {value.typeText && value.typeValues && (
                      <TextField
                        select={true}
                        defaultValue=""
                        onChange={(e) => {
                          setTypes({ ...types, ...{ [key]: e.target.value } });
                        }}
                        label={value.typeText}
                      >
                        {value.typeValues.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid item xs={5}>
                    {value.currencyText && value.currencyValues && (
                      <TextField
                        select={true}
                        defaultValue=""
                        onChange={(e) => {
                          setCurrency({ ...currency, ...{ [key]: e.target.value as Currency } });
                        }}
                        label={value.currencyText}
                      >
                        {value.currencyValues.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid container item xs={value.hedgingText ? 10 : 2} style={{ flexWrap: 'wrap' }}>
                    {value.hedgingText && (
                      <Grid item xs={12}>
                        {value.hedgingText} EUR
                      </Grid>
                    )}
                    {value.hedgingText &&
                      new Array(10).fill(0).map((mp, i) => (
                        <Grid item xs={3}>
                          <TextField
                            defaultValue={hedgingEur[i]}
                            onChange={(e) => {
                              setHedgingEur(hedgingEur.map((h, i2) => (i === i2 ? Number(e.target.value) : h)));
                            }}
                            label={`MP${i + 1}`}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Grid>
                      ))}
                    {value.hedgingText && (
                      <Grid item xs={12}>
                        {value.hedgingText} USD
                      </Grid>
                    )}
                    {value.hedgingText &&
                      new Array(10).fill(0).map((mp, i) => (
                        <Grid item xs={3}>
                          <TextField
                            defaultValue={hedgingUsd[i]}
                            onChange={(e) => {
                              setHedgingUsd(hedgingUsd.map((h, i2) => (i === i2 ? Number(e.target.value) : h)));
                            }}
                            label={`MP${i + 1}`}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <FileUpload
                    name={key}
                    accept=".csv"
                    onChange={(e: any) => onImportChange(e, key)}
                    disabled={
                      (value.typeText !== undefined && !types[key]) ||
                      (value.currencyText !== undefined && !currency[key])
                    }
                  />
                </Grid>
                <Grid item xs={12} />
              </React.Fragment>
            ))}
          </Grid>
        </Background>
      </Container>
    </>
  );
};

export default compose(inject('rootStore'), observer)(Import);
